import React, { ReactElement, useEffect } from 'react';
import { QueryClientProvider } from 'react-query';
import { AppRouter } from './app-router';
import { IdProvider } from 'react-use-id-hook';
import { BookingState } from './modules/booking-state/BookingState';
import { useFeatureServices } from './use-feature-services';
import queryClient from './modules/async/query-client';
import { BookingStateContextProvider } from './modules/booking-state/BookingStateContextProvider';
import styled from 'styled-components';
import { useObsConfig } from "./modules/obs-config/UseObsConfig";
import { setRecaptchaSiteKeyLoader } from "./recaptcha";
import { configureErrorReporting, installGlobalErrorHandlers, reportClientError } from "./modules/error-reporting/ErrorReporter";
import Version from "../../../package.json";

const StyledContainer = styled.div`
  position: relative;
`;

const defaultBookingContext: BookingState = {};

interface AppInfoVersions {
  backendVersion?: string;
  frontendVersion?: string;
  environment?: string;
  recaptchaSiteKey?: string | null;
}

export function App(): ReactElement {
  const { 's2:history': historyService } = useFeatureServices();
  const { tracking } = useFeatureServices();
  const obsConfig = useObsConfig();
  useEffect(() => {
    const unregister = historyService.history.listen(() => {
      console.log('history changes', historyService.history.location.pathname);
    });

    return unregister;
  });
  useEffect(() => {
    tracking.track('VWBasic_GenericFeatureApp_Load', {});
  }, []);

  // The backend address is only known from service-config, so the reporter is configured
  // here rather than at module level.
  useEffect(() => {
    configureErrorReporting(obsConfig.urlOrigin);

    return installGlobalErrorHandlers();
  }, [obsConfig.urlOrigin]);

  const frontendVersion = Version.version;

  useEffect(() => {
    const loadAppInfo = (): Promise<AppInfoVersions> =>
      // urlOrigin may carry a trailing slash; unlike the axios client this is a raw
      // fetch, so the join has to be normalised or the backend sees a 404 on `//api/...`.
      fetch(`${obsConfig.urlOrigin.replace(/\/+$/, '')}/api/AppInfo/versions`, {
        method: 'GET',
        headers: {
          'Frontend-Version': frontendVersion,
        },
      }).then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      });

    // The first reCAPTCHA set-up reuses the request made on start; once that has failed,
    // reCAPTCHA asks again on the next submit instead of giving up for the whole visit.
    let initialRequest: Promise<AppInfoVersions> | null = loadAppInfo();

    initialRequest.then(
      (data) => {
        console.log(`Backend Version: ${data.backendVersion}`);
        console.log(`Frontend Version: ${data.frontendVersion}`);
        console.log(`Environment: ${data.environment}`);
        (window as any).BackendVersion = data.backendVersion;
        (window as any).FrontendVersion = data.frontendVersion;
        (window as any).Environment = data.environment;
      },
      (error: unknown) => {
        initialRequest = null;
        console.error('Error fetching versions:', error);
        reportClientError({
          message: `Failed to load AppInfo versions: ${error instanceof Error ? error.message : String(error)}`,
          details: error instanceof Error ? error.stack : undefined,
          requestPath: '/api/AppInfo/versions',
        });
      },
    );

    setRecaptchaSiteKeyLoader(() => {
      const request = initialRequest ?? loadAppInfo();
      initialRequest = null;

      return request.then((data) => data.recaptchaSiteKey);
    });
  }, [obsConfig.urlOrigin, frontendVersion]);

  return (
    <StyledContainer>
      {/* @ts-ignore */}
      <IdProvider>
        <QueryClientProvider client={queryClient}>
          <BookingStateContextProvider defaultState={defaultBookingContext}>
            <AppRouter historyService={historyService} />
          </BookingStateContextProvider>
        </QueryClientProvider>
      </IdProvider>
    </StyledContainer>
  );
}
